export const PRODUCT_CODE_SUGGEST_PAGE_SIZE = 50;
export const PRODUCT_CODE_SUGGEST_DEBOUNCE_MS = 150;
export const PRODUCT_CODE_SUGGEST_SCROLL_THRESHOLD_PX = 48;

/**
 * @param {Array<Object>} products
 * @returns {Array<{ value: string, title: string, searchText: string }>}
 */
export function buildProductCodeSuggestOptions(products) {
    return (products || [])
        .filter((product) => product?.product_code)
        .map((product) => {
            const code = String(product.product_code).trim();
            const name = product.product_name || product.name || '';
            return {
                value: code,
                title: name ? `${code} — ${name}` : code,
                searchText: code.toLowerCase(),
            };
        })
        .sort((left, right) => left.value.localeCompare(right.value, 'ro'));
}

/**
 * @param {Array<{ searchText: string }>} options
 * @param {string} query
 * @param {{ offset?: number, limit?: number }} [paging]
 * @returns {{ matches: Array, hasMore: boolean }}
 */
export function collectProductCodeSuggestMatches(
    options,
    query,
    { offset = 0, limit = PRODUCT_CODE_SUGGEST_PAGE_SIZE } = {},
) {
    const normalizedQuery = String(query || '').trim().toLowerCase();
    if (!normalizedQuery) {
        return { matches: [], hasMore: false };
    }

    const matches = [];
    let skipped = 0;
    let hasMore = false;

    for (let index = 0; index < options.length; index += 1) {
        const option = options[index];
        if (!option.searchText.includes(normalizedQuery)) {
            continue;
        }
        if (skipped < offset) {
            skipped += 1;
            continue;
        }
        if (matches.length < limit) {
            matches.push(option);
            continue;
        }
        hasMore = true;
        break;
    }

    return { matches, hasMore };
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function resolveProductCodeSuggestValue(value) {
    if (value == null || value === '') {
        return '';
    }
    if (typeof value === 'object') {
        return String(value.value || value.product_code || '').trim();
    }
    return String(value).trim();
}

/**
 * @param {string} search
 * @param {string} selectedCode
 * @returns {boolean}
 */
export function isProductCodeSuggestSearchEcho(search, selectedCode) {
    const normalized = String(search || '').trim();
    const selected = String(selectedCode || '').trim();
    if (!selected || !normalized) {
        return false;
    }
    if (normalized === selected) {
        return true;
    }
    return normalized.startsWith(`${selected} —`) || normalized.startsWith(`${selected} -`);
}
